import { useEffect, useRef, useState } from 'react'
import { saveFileDraft, loadFileDraft, deleteFileDraft } from '../services/draftStore'

/**
 * Keeps a signup alive across a refresh, a dropped connection or a phone that swapped apps.
 *
 * Signup is the one long form filled by somebody who is NOT logged in, so the server-side
 * drafts used elsewhere (DraftBar) are not available to it — there is no account to hang a
 * draft on yet. This writes to the browser instead.
 *
 * WHAT IS NOT WRITTEN: passwords, ever. A password sitting in localStorage outlives the tab,
 * the session and often the person's memory of typing it; the two password boxes are the only
 * fields quick to retype anyway. `omit` names those keys.
 *
 * FILES. Any File in the state goes to IndexedDB, which can hold the bytes — localStorage
 * cannot. That matters on the open signup form, where the CV, the ID photo and the profile
 * picture are only in memory until submit, so a reload really did lose them.
 *
 * On the INVITE form the uploads have already reached the server against the invite token
 * (pending_{token}.*), so what a reload loses there is only the knowledge that they arrived —
 * the names, not the bytes. Those names are plain text and come back with everything else.
 *
 * Usage:
 *   const draft = useSignupDraft(key, { form, idNumber, ... }, { omit: ['password'] })
 *   useEffect(() => { if (draft.restored) applyEverything(draft.restored) }, [])
 *   ... draft.clear() on success
 */
const PREFIX = 'signup-draft:'
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000   // a week — an invite does not outlive that by much

export default function useSignupDraft(key, state, { omit = [], enabled = true } = {}) {
  const storageKey = PREFIX + key
  const [restored, setRestored] = useState(null)
  const [savedAt, setSavedAt] = useState(null)
  const loaded = useRef(false)

  // Read once, before anything is written, so the first save cannot erase what we came for.
  useEffect(() => {
    if (!enabled) { loaded.current = true; return }
    try {
      const raw = localStorage.getItem(storageKey)
      if (raw) {
        const { at, data } = JSON.parse(raw)
        if (at && Date.now() - at < MAX_AGE_MS && data && Object.keys(data).length) {
          setRestored(data)
          setSavedAt(at)
        } else {
          localStorage.removeItem(storageKey)
        }
      }
    } catch { /* corrupt or storage disabled — a blank form is the correct fallback */ }

    // Files live in IndexedDB, which is async, so they arrive a moment after the text and are
    // folded into whatever was restored. Best-effort throughout: no IndexedDB, no files back.
    loadFileDraft(storageKey).then(files => {
      if (files && Object.keys(files).length) {
        setRestored(prev => ({ ...(prev || {}), ...files }))
        setSavedAt(prev => prev || Date.now())
      }
    })

    loaded.current = true
  }, [storageKey, enabled])

  // Write on every change, debounced. Cheap enough that a slow network never comes into it:
  // this never touches the network, which is the entire point on a connection that is failing.
  useEffect(() => {
    if (!enabled || !loaded.current) return
    const id = setTimeout(() => {
      try {
        const data = {}
        const files = {}
        for (const [k, v] of Object.entries(state || {})) {
          if (omit.includes(k)) continue
          if (typeof File !== 'undefined' && v instanceof File) {
            files[k] = v
          } else if (v && typeof v === 'object' && !Array.isArray(v)) {
            const inner = Object.fromEntries(Object.entries(v).filter(([ik]) => !omit.includes(ik)))
            if (Object.values(inner).some(x => x !== '' && x != null)) data[k] = inner
          } else if (v !== '' && v != null && v !== false) {
            data[k] = v
          }
        }
        if (Object.keys(files).length) saveFileDraft(storageKey, files)
        if (!Object.keys(data).length) return
        localStorage.setItem(storageKey, JSON.stringify({ at: Date.now(), data }))
      } catch { /* quota or private mode — the form still works, it just will not survive */ }
    }, 600)
    return () => clearTimeout(id)
  }, [state, storageKey, enabled])   // eslint-disable-line react-hooks/exhaustive-deps

  const clear = () => {
    try { localStorage.removeItem(storageKey) } catch { /* nothing to do about it */ }
    deleteFileDraft(storageKey)
    setRestored(null)
  }

  const dismiss = () => setRestored(null)

  return { restored, savedAt, clear, dismiss }
}
